import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import {
  CreditCard,
  Heart,
  Copy,
  Check,
  DollarSign,
  Bitcoin,
  X,
  CheckCircle2,
  Wallet,
} from "lucide-react";

const CRYPTO_WALLETS = {
  BTC: "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
  ETH: "0x742d35Cc6634C0532925a3b844Bc9e7595f2bD85",
  USDT: "0x742d35Cc6634C0532925a3b844Bc9e7595f2bD85",
};

const DONATION_AMOUNTS = [25, 50, 100, 250, 500, 1000];

/* ───── Simulated Payment Modal ───── */
const SimulatedPaymentModal = ({ open, onClose, amount, method }) => {
  const [processing, setProcessing] = useState(false);

  if (!open) return null;

  const handleSimulatedPayment = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      onClose();
      toast.success(
        `Donation of $${amount.toLocaleString()} via ${method === "card" ? "Card" : "Crypto"} received! Thank you for your generosity.`,
        { duration: 6000 }
      );
    }, 1800);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200"
            data-testid="payment-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 className="font-heading font-bold text-lg text-slate-800">Confirm Donation</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="close-payment-modal">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Amount */}
              <div className="text-center py-4 rounded-xl bg-gradient-to-br from-[#6B21A8] to-[#3B82F6] text-white">
                <p className="text-sm opacity-80 mb-1">Donation Amount</p>
                <p className="text-4xl font-heading font-black">${amount.toLocaleString()}</p>
              </div>

              {/* Method */}
              {method === "card" ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <CreditCard className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Credit / Debit Card</p>
                      <p className="text-xs text-slate-400">Visa, Mastercard, Amex accepted</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                      <p className="text-xs text-slate-400">Card</p>
                      <p className="text-sm font-mono text-slate-600">**** 4242</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                      <p className="text-xs text-slate-400">Expires</p>
                      <p className="text-sm font-mono text-slate-600">12/28</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <Wallet className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Cryptocurrency</p>
                      <p className="text-xs text-slate-400">BTC, ETH, USDT supported</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                    <p className="text-xs text-slate-400">Network</p>
                    <p className="text-sm font-medium text-slate-600">Ethereum Mainnet</p>
                  </div>
                </div>
              )}

              {/* Action */}
              <Button
                className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white h-12 text-base font-semibold"
                onClick={handleSimulatedPayment}
                disabled={processing}
                data-testid="confirm-payment-btn"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Confirm ${amount.toLocaleString()} Donation
                  </span>
                )}
              </Button>

              <p className="text-[11px] text-slate-400 text-center">
                This is a simulated transaction for demonstration purposes.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ───── Donation Form ───── */
export const DonationForm = ({ project }) => {
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const handleCopyWallet = (currency, address) => {
    navigator.clipboard.writeText(address);
    setCopiedWallet(currency);
    toast.success(`${currency} address copied!`);
    setTimeout(() => setCopiedWallet(null), 3000);
  };

  const handleDonate = () => {
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setModalOpen(true);
  };

  return (
    <>
      <Card className="border border-slate-200 shadow-sm" data-testid="donation-form">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-slate-800">
            <Heart className="h-5 w-5 text-primary" />
            Make a Donation
          </CardTitle>
          <CardDescription className="text-slate-500">
            Support {project.title} and make a difference
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="card" data-testid="payment-tab-card">
                <CreditCard className="h-4 w-4 mr-2" />
                Card Payment
              </TabsTrigger>
              <TabsTrigger value="crypto" data-testid="payment-tab-crypto">
                <Bitcoin className="h-4 w-4 mr-2" />
                Crypto
              </TabsTrigger>
            </TabsList>

            <TabsContent value="card" className="space-y-6">
              {/* Amount Selection */}
              <div className="space-y-3">
                <Label className="text-slate-700">Select Amount (USD)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {DONATION_AMOUNTS.map((amt) => (
                    <Button
                      key={amt}
                      type="button"
                      variant={selectedAmount === amt && !customAmount ? "default" : "outline"}
                      className={selectedAmount === amt && !customAmount ? "bg-primary text-white" : "border-slate-200 text-slate-600"}
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }}
                      data-testid={`amount-${amt}`}
                    >
                      ${amt}
                    </Button>
                  ))}
                </div>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(0); }}
                    className="pl-10 border-slate-200"
                    min="1"
                    data-testid="custom-amount"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="anonymous"
                    checked={isAnonymous}
                    onCheckedChange={setIsAnonymous}
                    data-testid="anonymous-checkbox"
                  />
                  <Label htmlFor="anonymous" className="text-sm text-slate-600">
                    Make this donation anonymous
                  </Label>
                </div>

                {!isAnonymous && (
                  <div>
                    <Label htmlFor="donorName" className="text-slate-700">Your Name</Label>
                    <Input
                      id="donorName"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="John Doe"
                      className="border-slate-200"
                      data-testid="donor-name"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="donorEmail" className="text-slate-700">Email (for receipt)</Label>
                  <Input
                    id="donorEmail"
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="border-slate-200"
                    data-testid="donor-email"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700">Message (optional)</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Leave a message of support..."
                    rows={3}
                    className="border-slate-200"
                    data-testid="donation-message"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Donation Amount</span>
                  <span className="text-2xl font-bold text-primary">
                    ${(amount || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <Button
                className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white h-12 text-base font-semibold"
                onClick={handleDonate}
                disabled={!amount || amount <= 0}
                data-testid="donate-now-btn"
              >
                <Heart className="h-5 w-5 mr-2" />
                Donate Now
              </Button>
            </TabsContent>

            <TabsContent value="crypto" className="space-y-6">
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  Send cryptocurrency directly to our wallet addresses. After sending,
                  the donation will be verified and added to the project.
                </p>

                {Object.entries(CRYPTO_WALLETS).map(([currency, address]) => (
                  <div key={currency} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">{currency}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyWallet(currency, address)}
                        data-testid={`copy-${currency}`}
                      >
                        {copiedWallet === currency ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                    <code className="text-xs text-slate-500 break-all">{address}</code>
                  </div>
                ))}

                <Button
                  className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white h-12 text-base font-semibold"
                  onClick={handleDonate}
                  data-testid="donate-crypto-btn"
                >
                  <Wallet className="h-5 w-5 mr-2" />
                  Simulate Crypto Donation
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Simulated Payment Modal */}
      <SimulatedPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        amount={amount || 0}
        method={paymentMethod}
      />
    </>
  );
};

export default DonationForm;
